import { OTPVerificationScreen } from '@/components/organisms';
import { logger } from '@/core/logger';
import { useAppStore } from '@/core/stores/appStore';
import { useManualRegistrationStore } from '@/modules/onboarding/registration/store/registrationStore';
import { customerDetailsService } from '@/modules/onboarding/services/customerDetailsService';
import { router } from 'expo-router';
import React, { useCallback, useEffect } from 'react';
import { useOTP } from '../hooks/useOTP';
import { trackDynatraceEvent } from '@/core/services/dynatraceService';

export interface EmailOTPScreenProps {
    email?: string;
    progress?: number;
}

export const EmailOTPScreen = React.memo<EmailOTPScreenProps>(function EmailOTPScreen({
    email,
    progress = 0.8,
}) {
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    const manualRegistration = useManualRegistrationStore((state) => state.registrationResponse);
    const user = uaePassRegistration?.user || null;
    const derivedEmail = email ?? user?.email ?? '';

    // Determine if this is manual registration or UAE Pass registration
    const isManualRegistration = !!manualRegistration && !uaePassRegistration;

    const {
        isLoading,
        error,
        isVerified,
        verifyOtp,
        resendOtp,
        clearError,
    } = useOTP({
        type: 'email',
        contactInfo: derivedEmail,
        autoSend: true,
        sectionName: 'emailOtp',
    });

    useEffect(() => {
        trackDynatraceEvent({
            p_screen: 'otp',
            p_actionName: 'Email OTP Screen Viewed',
            p_sectionName: 'emailOtp',
            eventType: 'view',
        });
    }, []);

    const handleOTPComplete = useCallback(async (otp: string) => {
        logger.info('EmailOTPScreen', 'OTP entered, verifying...', { otp });
        const verified = await verifyOtp(otp);

        if (verified) {
            logger.info('EmailOTPScreen', 'Email OTP verified successfully, calling verify-details API');

            // Get registration data from appStore
            const currentState = useAppStore.getState();
            const registration = currentState.uaePassRegistration;

            if (!registration?.customerId || !registration?.registrationId || !user?.email) {
                logger.error('EmailOTPScreen', 'Missing required data for verify-details API', {
                    hasCustomerId: !!registration?.customerId,
                    hasRegistrationId: !!registration?.registrationId,
                    hasEmail: !!user?.email,
                });
                // Navigate based on registration type
                if (isManualRegistration) {
                    router.push('/document-review');
                } else {
                    router.replace('/registration-success');
                }
                return;
            }

            // Parse phone number to extract country code and mobile number (if available)
            let countryCode: string | undefined;
            let mobile: string | undefined;

            if (user.mobile) {
                const phoneStr = user.mobile.replace(/\s+/g, '');

                // Extract country code if phone starts with +
                if (phoneStr.startsWith('+')) {
                    const codeMatch = phoneStr.match(/^\+\d{1,3}/)?.[0];
                    if (codeMatch) {
                        countryCode = codeMatch.slice(1); // Remove + sign
                        mobile = phoneStr.replace(/^\+\d{1,3}/, '');
                    }
                } else {
                    // Try to match known country codes
                    const knownCodes = ['971', '1', '44', '33', '49', '39', '34', '86', '81', '82', '91', '61', '55', '52', '7', '27'];
                    const matchedCode = knownCodes.find(cc => phoneStr.startsWith(cc));
                    if (matchedCode) {
                        countryCode = matchedCode;
                        mobile = phoneStr.slice(matchedCode.length);
                    } else {
                        // Default to UAE if no match
                        countryCode = '971';
                        mobile = phoneStr;
                    }
                }
            }

            try {
                // Call verify-details API
                await customerDetailsService.verifyDetails({
                    customer_id: registration.customerId,
                    ...(countryCode && { country_code: countryCode }),
                    ...(mobile && { mobile: mobile }),
                    email: user.email,
                    registration_id: registration.registrationId,
                });
                logger.info('EmailOTPScreen', 'Verify-details API called successfully, navigating to registration success');
            } catch (error) {
                logger.error('EmailOTPScreen', 'Failed to call verify-details API', error);
                // Continue navigation even if API call fails
            }

            // Navigate based on registration type
            // Manual registration: go to document-review (use push so back button works)
            // UAE Pass registration: go directly to registration-success
            if (isManualRegistration) {
                logger.info('EmailOTPScreen', 'Manual registration detected, navigating to document-review');
                router.push('/document-review');
            } else {
                logger.info('EmailOTPScreen', 'UAE Pass registration detected, navigating to registration-success');
                router.replace('/registration-success');
            }
        } else {
            logger.warn('EmailOTPScreen', 'Email OTP verification failed');
        }
    }, [verifyOtp, user, isManualRegistration]);

    const handleResendOTP = useCallback(async () => {
        logger.info('EmailOTPScreen', 'Resending email OTP');
        clearError();
        await resendOtp();
    }, [resendOtp, clearError]);

    // Clear error when OTP input changes
    const handleOTPChange = useCallback(() => {
        if (error) {
            clearError();
        }
    }, [error, clearError]);

    return (
        <OTPVerificationScreen
            type="email"
            contactInfo={derivedEmail}
            progress={progress}
            otpLength={4}
            onOTPComplete={handleOTPComplete}
            onResendOTP={handleResendOTP}
            onOTPChange={handleOTPChange}
            verifyingOTP={isLoading}
            otpError={error}
            otpSuccess={isVerified}
        />
    );
});

export default EmailOTPScreen;